import PhotoSwipeLightbox from './photoswipe-lightbox.esm.js';

// Mac trackpad pinch is delivered as pixel-mode ctrl+wheel in Chromium.
// PhotoSwipe's mouse-wheel coefficient makes those small deltas barely zoom.
// Keep mouse-wheel speed and zoom limits, but give pinch a direct response.
// During a gesture update only the transform; commit the sharp image size once
// the gesture ends, rather than resizing a multi-megapixel image on every event.
export function installPrecisionZoom(lightbox, clock = {
  frame: (fn) => requestAnimationFrame(fn),
  cancelFrame: (id) => cancelAnimationFrame(id),
  delay: (fn) => setTimeout(fn, 150),
  cancelDelay: (id) => clearTimeout(id)
}) {
  let slide = null;
  let targetZoom = 0;
  let point = null;
  let frame = null;
  let timer = null;

  const clearScheduled = () => {
    if (frame !== null) clock.cancelFrame(frame);
    if (timer !== null) clock.cancelDelay(timer);
    frame = timer = null;
  };
  const cancel = () => {
    clearScheduled();
    slide = point = null;
  };
  const render = () => {
    frame = null;
    if (!slide || lightbox.pswp?.currSlide !== slide) return;
    const oldZoom = slide.currZoomLevel;
    if (oldZoom === targetZoom) return;
    slide.setZoomLevel(targetZoom);
    const ratio = targetZoom / oldZoom;
    slide.panTo(
      (slide.pan.x - point.x) * ratio + point.x,
      (slide.pan.y - point.y) * ratio + point.y
    );
  };
  const finish = () => {
    clearScheduled();
    render();
    const finishedSlide = slide;
    slide = point = null;
    if (finishedSlide && lightbox.pswp?.currSlide === finishedSlide) {
      // Public API commits the final resolution without changing the source.
      finishedSlide.zoomTo(finishedSlide.currZoomLevel, undefined, 0);
    }
  };

  lightbox.on('wheel', (event) => {
    const e = event.originalEvent;
    const pswp = lightbox.pswp;
    const current = pswp?.currSlide;
    if (!current || (!e.ctrlKey && !pswp.options.wheelToZoom)) return;
    event.preventDefault(); // Don't also run PhotoSwipe's per-event resize path.
    if (!pswp.opener.isOpen || pswp.mainScroll.isShifted()
        || !current.isZoomable() || !Number.isFinite(e.deltaY) || e.deltaY === 0) return;

    if (slide !== current) {
      cancel();
      pswp.animations.stopAllPan();
      slide = current;
      targetZoom = current.currZoomLevel;
    }
    const coefficient = e.ctrlKey && e.deltaMode === 0
      ? 0.01
      : Math.LN2 * (e.deltaMode === 1 ? 0.05 : e.deltaMode ? 1 : 0.002);
    const factor = Math.exp(Math.max(-4, Math.min(4, -e.deltaY * coefficient)));
    targetZoom = Math.max(current.zoomLevels.min,
      Math.min(current.zoomLevels.max, targetZoom * factor));
    point = { x: e.clientX, y: e.clientY };
    if (frame === null) frame = clock.frame(render);
    if (timer !== null) clock.cancelDelay(timer);
    timer = clock.delay(finish);
  });
  // Never let delayed work from an old slide/gesture affect a new one.
  lightbox.on('pointerDown', finish);
  lightbox.on('beforeZoomTo', finish);
  lightbox.on('beforeResize', cancel);
  lightbox.on('change', cancel);
  lightbox.on('close', cancel);
  lightbox.on('destroy', cancel);
}

const gallery = document.querySelector('[data-product-gallery]');

if (gallery) {
  const lightbox = new PhotoSwipeLightbox({
    gallery: '[data-pswp-gallery]',
    children: 'a[data-pswp-item]',
    pswpModule: () => import('./photoswipe.esm.js'),
    bgOpacity: 0.96,
    wheelToZoom: true,
    preload: [0, 1],
    paddingFn: (viewportSize) => ({
      top: viewportSize.x < 700 ? 20 : 40,
      bottom: viewportSize.x < 700 ? 20 : 40,
      left: viewportSize.x < 700 ? 12 : 40,
      right: viewportSize.x < 700 ? 12 : 40
    })
  });
  installPrecisionZoom(lightbox);
  lightbox.init();

  const slides = Array.from(gallery.querySelectorAll('.product-gallery-item'));
  const current = document.querySelector('[data-product-gallery-current]');

  if (slides.length && current && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      const index = slides.indexOf(visible.target);
      if (index >= 0) current.textContent = String(index + 1).padStart(2, '0');
    }, { root: gallery, threshold: [0.55, 0.75] });

    slides.forEach((slide) => observer.observe(slide));
  }
}

document.querySelectorAll('[data-product-film-toggle]').forEach((button) => {
  const figure = button.closest('.product-gallery-item--video');
  const video = figure?.querySelector('.product-film');
  const state = button.querySelector('[data-product-film-state]');
  if (!video) return;

  button.addEventListener('click', async () => {
    if (video.paused) {
      await video.play();
      button.setAttribute('aria-label', 'Pause Catalina Island film');
      if (state) state.textContent = 'Pause film';
    } else {
      video.pause();
      button.setAttribute('aria-label', 'Play Catalina Island film');
      if (state) state.textContent = 'Play film';
    }
  });

  video.addEventListener('ended', () => {
    button.setAttribute('aria-label', 'Play Catalina Island film');
    if (state) state.textContent = 'Play film';
  });
});
